import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Row,
  Col,
  Spin,
  Space,
  Typography,
  Empty,
  Table,
  Tag,
  Checkbox,
  Modal,
  Input,
} from 'antd';
import { MessageOutlined, SendOutlined, LeftOutlined, RightOutlined, FilePdfOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { setMessage } from '../../../redux/message.reducer';
import { linkifyText } from '../../../utils/linkify';
import CommentAttachments from '../../../components/CommentAttachments/CommentAttachments';
import CommentAttachmentInput from '../../../components/CommentAttachments/CommentAttachmentInput';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import '../../order/edit/OrderEdit.css';
import '../../order/view/OrderView.css';

const QUALITY_LABELS = [
  { key: 'qualityGhatTheek', label: 'घाट ठीक है' },
  { key: 'qualityStoneSahi', label: 'स्टोन सही है' },
  { key: 'qualityOilLaga', label: 'ऑयल लगा दिया' },
  { key: 'qualitySettingTheek', label: 'सेटिंग ठीक है' },
  { key: 'qualityNoScratch', label: 'कोई स्क्रैच नहीं है' },
  { key: 'qualityPolishTheek', label: 'पॉलिश ठीक है' },
  { key: 'qualityRhodiumTheek', label: 'रोडियम ठीक है' },
  { key: 'qualityEarringPush', label: 'इयररिंग का पुश सही काम कर रहा है' },
  { key: 'qualityLockBand', label: 'लॉक सही से बंद हो रहा है' },
];

const DISPATCHED_COLORS = { inprogress: 'orange', half: 'blue', full: 'green' };
const ORDER_DISPATCHED_LABELS = { inprogress: 'In progress', half: 'Half', full: 'Full' };
const ORDER_STATUS_LABELS = { open: 'Open', inprogress: 'In progress', ready: 'Ready', dispatch: 'Dispatch' };

/** Load image from URL and return data URL for use in jsPDF. Returns null on CORS/load error. */
function loadImageAsDataUrl(url) {
  return new Promise((resolve) => {
    if (!url || typeof url !== 'string') {
      resolve(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function ImageGallery({ images, title }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const list = Array.isArray(images) ? images : [];
  if (list.length === 0) return <span className="order-view-empty-text">—</span>;
  return (
    <div className="order-view-image-gallery">
      <div className="order-view-image-thumbs">
        {list.map((url, index) => (
          <button
            key={index}
            type="button"
            className="order-view-image-thumb"
            onClick={() => { setPreviewIndex(index); setPreviewOpen(true); }}
          >
            <img src={url} alt={`${title} ${index + 1}`} />
          </button>
        ))}
      </div>
      <Modal
        title={`${title} ${previewIndex + 1} of ${list.length}`}
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width="auto"
        centered
        styles={{ body: { padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 } }}
      >
        {list.length > 0 && (
          <>
            {previewIndex > 0 && (
              <Button type="text" icon={<LeftOutlined />} onClick={() => setPreviewIndex((i) => i - 1)} />
            )}
            <img
              src={list[previewIndex]}
              alt={`${title} ${previewIndex + 1}`}
              style={{ maxHeight: '80vh', maxWidth: '70vw', objectFit: 'contain' }}
            />
            {previewIndex < list.length - 1 && (
              <Button type="text" icon={<RightOutlined />} onClick={() => setPreviewIndex((i) => i + 1)} />
            )}
          </>
        )}
      </Modal>
    </div>
  );
}

export default function BulkOrderView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [bulkOrder, setBulkOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [commentLoading, setCommentLoading] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [selectedPdfIndices, setSelectedPdfIndices] = useState([]);
  const chatMessagesRef = useRef(null);
  const { user: currentUser } = useSelector((state) => state.authReducer);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    axios
      .get(`/bulk-orders/${id}`)
      .then((res) => {
        if (!cancelled) setBulkOrder(res.data);
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = err.response?.data?.error || 'Failed to load bulk order';
          dispatch(setMessage({ message: msg, type: 'error' }));
          navigate('/bulk-orders');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id, navigate, dispatch]);

  const handleAddComment = async () => {
    const trimmed = commentText?.trim();
    if (!trimmed) return;
    setCommentLoading(true);
    try {
      const res = await axios.post(`/bulk-orders/${id}/comments`, {
        text: trimmed,
        attachments: commentAttachments,
      });
      setBulkOrder(res.data);
      setCommentText('');
      setCommentAttachments([]);
      dispatch(setMessage({ message: 'Comment added', type: 'success' }));
      setTimeout(() => { if (chatMessagesRef.current) chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight; }, 100);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to add comment';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setCommentLoading(false);
    }
  };

  const openPdfModal = () => {
    const list = Array.isArray(bulkOrder?.orderList) ? bulkOrder.orderList : [];
    setSelectedPdfIndices(list.map((_, i) => i));
    setPdfModalOpen(true);
  };

  const togglePdfItem = (index) => {
    setSelectedPdfIndices((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index].sort((a, b) => a - b)
    );
  };

  const selectAllPdfItems = () => {
    const list = Array.isArray(bulkOrder?.orderList) ? bulkOrder.orderList : [];
    setSelectedPdfIndices(list.map((_, i) => i));
  };

  const clearAllPdfItems = () => setSelectedPdfIndices([]);

  const generatePdf = async () => {
    const list = Array.isArray(bulkOrder?.orderList) ? bulkOrder.orderList : [];
    const selected = selectedPdfIndices.filter((i) => i >= 0 && i < list.length).sort((a, b) => a - b);
    if (selected.length === 0) {
      dispatch(setMessage({ message: 'Select at least one item', type: 'error' }));
      return;
    }
    const items = selected.map((i) => list[i]);
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 14;
    const title = bulkOrder.bulkOrderNumber ? `Bulk Order #${bulkOrder.bulkOrderNumber}` : 'Bulk Order';
    // Load all item images first
    const imageTasks = [];
    items.forEach((it, idx) => {
      const urls = Array.isArray(it.photoOrFile) ? it.photoOrFile : [];
      urls.forEach((url) => imageTasks.push({ itemIndex: selected[idx] + 1, url, promise: loadImageAsDataUrl(url) }));
    });
    const imageResults = await Promise.all(imageTasks.map(async (t) => ({ itemIndex: t.itemIndex, dataUrl: await t.promise })));
    const imagesByItem = {};
    imageResults.forEach((r, i) => {
      if (r.dataUrl) {
        const key = imageTasks[i].itemIndex;
        if (!imagesByItem[key]) imagesByItem[key] = [];
        imagesByItem[key].push(r.dataUrl);
      }
    });

    // Card layout: one card per item with image(s) on left, text on right
    const cardGap = 8;
    const cardPad = 6;
    const thumbSize = 42;
    const thumbGap = 6;
    const imgCols = 2;
    const imageBlockRight = margin + cardPad + imgCols * (thumbSize + thumbGap);
    const textGap = 28;
    const textX = imageBlockRight + textGap;
    const textMaxWidth = pageW - margin - textX - 10;
    const lineH = 8;
    const maxImagesPerItem = 5;
    const imgRows = Math.ceil(maxImagesPerItem / imgCols);
    const imgBlockHeight = imgRows * (thumbSize + thumbGap);

    const splitToLines = (str, maxW) => {
      if (typeof doc.splitTextToSize === 'function') {
        return doc.splitTextToSize(str, maxW);
      }
      if (typeof doc.getStringUnitWidth === 'function') {
        const words = str.split(/\s+/);
        const lines = [];
        let line = '';
        for (let i = 0; i < words.length; i++) {
          const test = line ? `${line} ${words[i]}` : words[i];
          const w = doc.getStringUnitWidth(test);
          if (w > maxW && line) {
            lines.push(line);
            line = words[i];
          } else {
            line = test;
          }
        }
        if (line) lines.push(line);
        return lines.length ? lines : [str];
      }
      return [str];
    };

    let y = 22;
    let addedPageForFirstItem = false;
    doc.setFontSize(11);
    items.forEach((it, idx) => {
      const itemNum = selected[idx] + 1;

      const descStr = String(`Description: ${(it.itemDescription || '—')}`);
      const descLinesArray = splitToLines(descStr, textMaxWidth);
      const remarkStr = String(`Remark: ${((it.remark || '').trim()) || '—'}`);
      const remarkLines = splitToLines(remarkStr, textMaxWidth);

      const textBlockLines = 1 + descLinesArray.length + 1 + remarkLines.length;
      const cardHeight = cardPad + 8 + imgBlockHeight + 2 + lineH * textBlockLines + cardPad;

      if (y + cardHeight > pageH - margin) {
        doc.addPage([pageW, pageH], 'l');
        y = margin;
        if (idx === 0) addedPageForFirstItem = true;
      }

      if (idx === 0) {
        doc.setFontSize(16);
        doc.text(String(title), Number(margin), 15);
        doc.setFontSize(11);
        if (y === margin) y = 22;
      }

      const cardY = y;
      const cardW = pageW - 2 * margin;

      doc.setFillColor(248, 248, 248);
      doc.rect(margin, cardY, cardW, cardHeight, 'FD');
      doc.setDrawColor(220, 220, 220);
      doc.rect(margin, cardY, cardW, cardHeight, 'S');

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(String(`Item ${itemNum}`), margin + cardPad, cardY + cardPad + 4);
      doc.setFont('helvetica', 'normal');

      const dataUrls = imagesByItem[itemNum] || [];
      const imgStartY = cardY + cardPad + 8;
      if (dataUrls.length > 0) {
        dataUrls.slice(0, maxImagesPerItem).forEach((dataUrl, i) => {
          const row = Math.floor(i / 2);
          const col = i % 2;
          const ix = margin + cardPad + col * (thumbSize + thumbGap);
          const iy = imgStartY + row * (thumbSize + thumbGap);
          doc.setDrawColor(200, 200, 200);
          doc.rect(ix, iy, thumbSize, thumbSize, 'S');
          doc.addImage(dataUrl, 'JPEG', ix, iy, thumbSize, thumbSize);
        });
      }

      doc.setFontSize(11);
      let ty = imgStartY + 2;
      doc.text(String(`Item name: ${(it.itemName || '—').slice(0, 40)}`), textX, ty, { maxWidth: textMaxWidth });
      ty += lineH;
      doc.text(descLinesArray, textX, ty);
      ty += lineH * descLinesArray.length;
      doc.text(String(`Qty: ${it.quantity ?? '—'}`), textX, ty);
      doc.text(String(`Delivery: ${it.deliveryDate ? dayjs(it.deliveryDate).format('MMM D, YYYY') : '—'}`), textX + 42, ty);
      ty += lineH;
      doc.text(remarkLines, textX, ty, { maxWidth: textMaxWidth });
      ty += lineH * remarkLines.length;

      y = cardY + cardHeight + cardGap;
    });

    if (addedPageForFirstItem && doc.internal.getNumberOfPages() > 1) {
      doc.deletePage(1);
    }

    const filename = `BulkOrder-${(bulkOrder.bulkOrderNumber || id || 'export').replace(/\s+/g, '-')}.pdf`;
    doc.save(filename);
    setPdfModalOpen(false);
    dispatch(setMessage({ message: 'PDF downloaded', type: 'success' }));
  };

  if (loading) {
    return (
      <div className="order-edit-page order-view-page">
        <div className="order-edit-loading">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (!bulkOrder) return null;

  const { Title } = Typography;
  const orderList = Array.isArray(bulkOrder.orderList) ? bulkOrder.orderList : [];
  const parcelImages = Array.isArray(bulkOrder.parcelImages) ? bulkOrder.parcelImages : [];
  const marking = Array.isArray(bulkOrder.marking) ? bulkOrder.marking : [];

  const orderListColumns = [
    {
      title: 'Photos',
      dataIndex: 'photoOrFile',
      key: 'photoOrFile',
      width: 240,
      render: (urls, record, rowIndex) => (
        <div className="order-view-table-photos">
          <ImageGallery images={Array.isArray(urls) ? urls : []} title={`Item ${rowIndex + 1}`} />
        </div>
      ),
    },
    { title: 'Item name', dataIndex: 'itemName', key: 'itemName', width: 120, render: (v) => v || '—' },
    {
      title: 'Description',
      dataIndex: 'itemDescription',
      key: 'itemDescription',
      width: 300,
      ellipsis: false,
      render: (v) => (
        <span className="order-view-table-cell-wrap" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {v || '—'}
        </span>
      ),
    },
    { title: 'Qty', dataIndex: 'quantity', key: 'quantity', width: 70, render: (v) => v ?? '—' },
    { title: 'Price/piece', dataIndex: 'pricePerPiece', key: 'pricePerPiece', width: 100, render: (v) => (v != null ? `₹${Number(v).toLocaleString()}` : '—') },
    { title: 'Delivery date', dataIndex: 'deliveryDate', key: 'deliveryDate', width: 110, render: (v) => (v ? dayjs(v).format('MMM D, YYYY') : '—') },
    { title: 'Order status', dataIndex: 'orderStatus', key: 'orderStatus', width: 100, render: (v) => (v ? (ORDER_STATUS_LABELS[v] ?? String(v).charAt(0).toUpperCase() + String(v).slice(1)) : '—') },
    {
      title: 'Remark',
      dataIndex: 'remark',
      key: 'remark',
      width: 220,
      minWidth: 180,
      ellipsis: false,
      render: (v) => (
        <span className="order-view-table-cell-wrap" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {v != null && String(v).trim() ? String(v).trim() : '—'}
        </span>
      ),
    },
  ];

  return (
    <div className="order-edit-page order-view-page">
      <Space direction="vertical" size="large" style={{ width: '100%' }} className="order-view-space">
        <div className="order-view-header order-view-header-bar">
          <div className="order-view-header-title-wrap">
            <Title level={3} className="order-view-title">
              {bulkOrder.bulkOrderNumber ? `Bulk Order #${bulkOrder.bulkOrderNumber}` : 'View bulk order'}
            </Title>
            <p className="order-view-subtitle">{bulkOrder.customerName || '—'}</p>
            <div className="order-view-badges">
              <Tag color={DISPATCHED_COLORS[bulkOrder.orderDispatched] || 'default'}>
                {bulkOrder.orderDispatched ? (ORDER_DISPATCHED_LABELS[bulkOrder.orderDispatched] ?? String(bulkOrder.orderDispatched).charAt(0).toUpperCase() + String(bulkOrder.orderDispatched).slice(1)) : 'In progress'}
              </Tag>
            </div>
          </div>
          <Space size="small" align="start" wrap>
            <Button
              icon={<FilePdfOutlined />}
              onClick={openPdfModal}
              disabled={orderList.length === 0}
              title="Generate PDF"
              aria-label="Generate PDF"
            >
            </Button>
            <Button onClick={() => navigate(-1)} size="large" className="app-btn-cancel">
            <LeftOutlined /> Back
          </Button>
          </Space>
        </div>

        <Card className="order-view-card order-view-card-details">
          <div className="order-view-card-head">
            <Title level={5} className="order-view-section-title">Bulk order details</Title>
          </div>
          <Row gutter={[24, 20]} className="order-view-details-grid">
            <Col xs={24} sm={12} md={8}>
              <div className="order-view-field">
                <span className="order-view-label">Customer name</span>
                <span className="order-view-value">{bulkOrder.customerName ?? '—'}</span>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="order-view-field">
                <span className="order-view-label">Order amount</span>
                <span className="order-view-value">
                  {bulkOrder.orderAmount != null ? `₹${Number(bulkOrder.orderAmount).toLocaleString('en-IN')}` : '—'}
                </span>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="order-view-field">
                <span className="order-view-label">Mode of payment</span>
                <span className="order-view-value">{bulkOrder.modeOfPayment ?? '—'}</span>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="order-view-field">
                <span className="order-view-label">Concerned person</span>
                <span className="order-view-value">{bulkOrder.concernedPerson ?? '—'}</span>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="order-view-field">
                <span className="order-view-label">Order date</span>
                <span className="order-view-value">{bulkOrder.orderDate ? dayjs(bulkOrder.orderDate).format('MMM D, YYYY') : '—'}</span>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="order-view-field">
                <span className="order-view-label">Target date</span>
                <span className="order-view-value">{bulkOrder.targetDate ? dayjs(bulkOrder.targetDate).format('MMM D, YYYY') : '—'}</span>
              </div>
            </Col>
          </Row>
        </Card>

        <Card className="order-view-card order-view-card-order-list">
          <div className="order-view-card-head">
            <Title level={5} className="order-view-section-title">Order list</Title>
          </div>
          {orderList.length > 0 ? (
            <>
              <div className="order-view-order-list-table">
                <Table
                  dataSource={orderList.map((row, idx) => ({ ...row, key: row._id || idx }))}
                  columns={orderListColumns}
                  pagination={false}
                  size="small"
                  scroll={{ x: 'max-content' }}
                />
              </div>
              <div className="order-view-order-list-cards">
                {orderList.map((item, idx) => (
                  <div key={item._id || idx} className="order-view-order-card">
                    <div className="order-view-order-card-head">
                      <span className="order-view-order-card-title">Item {idx + 1}</span>
                      {item.orderStatus ? (
                        <Tag color={DISPATCHED_COLORS[item.orderStatus] || 'default'}>
                          {ORDER_STATUS_LABELS[item.orderStatus] ?? String(item.orderStatus).charAt(0).toUpperCase() + String(item.orderStatus).slice(1)}
                        </Tag>
                      ) : null}
                    </div>
                    <div className="order-view-order-card-body">
                      <div className="order-view-order-card-field order-view-order-card-photos">
                        <span className="order-view-label">Photos</span>
                        <div className="order-view-order-card-value">
                          <ImageGallery images={Array.isArray(item.photoOrFile) ? item.photoOrFile : []} title={`Item ${idx + 1}`} />
                        </div>
                      </div>
                      <div className="order-view-order-card-field">
                        <span className="order-view-label">Item name</span>
                        <span className="order-view-value">{item.itemName || '—'}</span>
                      </div>
                      <div className="order-view-order-card-field">
                        <span className="order-view-label">Description</span>
                        <span className="order-view-value">{item.itemDescription || '—'}</span>
                      </div>
                      <div className="order-view-order-card-row">
                        <div className="order-view-order-card-field">
                          <span className="order-view-label">Qty</span>
                          <span className="order-view-value">{item.quantity ?? '—'}</span>
                        </div>
                        <div className="order-view-order-card-field">
                          <span className="order-view-label">Price/piece</span>
                          <span className="order-view-value">
                            {item.pricePerPiece != null ? `₹${Number(item.pricePerPiece).toLocaleString()}` : '—'}
                          </span>
                        </div>
                      </div>
                      <div className="order-view-order-card-field">
                        <span className="order-view-label">Delivery date</span>
                        <span className="order-view-value">
                          {item.deliveryDate ? dayjs(item.deliveryDate).format('MMM D, YYYY') : '—'}
                        </span>
                      </div>
                      <div className="order-view-order-card-field">
                        <span className="order-view-label">Remark</span>
                        <span className="order-view-value">{item.remark != null && String(item.remark).trim() ? String(item.remark).trim() : '—'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <Empty description="No order lines" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Card>

        <Card className="order-view-card order-view-card-quality">
          <div className="order-view-card-head">
            <Title level={5} className="order-view-section-title">Quality check</Title>
          </div>
          <Row gutter={[24, 20]}>
            <Col xs={24} md={12}>
              <div className="order-view-quality-list">
                {QUALITY_LABELS.slice(0, 7).map(({ key, label }) => (
                  <div key={key} className="order-view-quality-item">
                    <Checkbox checked={bulkOrder[key] === true} disabled />
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div className="order-view-quality-list">
                {QUALITY_LABELS.slice(7, 9).map(({ key, label }) => (
                  <div key={key} className="order-view-quality-item">
                    <Checkbox checked={bulkOrder[key] === true} disabled />
                    <span>{label}</span>
                  </div>
                ))}
              </div>
              <div className="order-view-field order-view-field-inline">
                <span className="order-view-label">रिंग साइज</span>
                <span className="order-view-value">{bulkOrder.ringSize || '—'}</span>
              </div>
              <div className="order-view-field order-view-field-inline">
                <span className="order-view-label">समान की गिनती</span>
                <span className="order-view-value">{bulkOrder.samaanGinti || '—'}</span>
              </div>
            </Col>
            <Col xs={24}>
              <div className="order-view-field">
                <span className="order-view-label">मार्किंग</span>
                <span className="order-view-value">{marking.length > 0 ? marking.join(', ') : '—'}</span>
              </div>
            </Col>
            <Col xs={24}>
              <div className="order-view-field order-view-field-images">
                <span className="order-view-label">पार्सल तस्वीर</span>
                <ImageGallery images={parcelImages} title="Parcel photo" />
              </div>
            </Col>
          </Row>
        </Card>

        <Card title={<span><MessageOutlined style={{ marginRight: 8 }} />Comments</span>} className="order-view-card order-edit-comments-card">
          <div className="order-edit-chat">
            <div className="order-edit-chat-messages" ref={chatMessagesRef}>
              {bulkOrder.comments && bulkOrder.comments.length > 0 ? (
                bulkOrder.comments.map((item, idx) => {
                  const isSystem = !item.created_by;
                  const name = item.created_by ? (item.created_by.name || item.created_by.email || '—') : 'Kosha';
                  const initial = (name !== '—' && name !== 'Kosha' ? name.charAt(0) : isSystem ? '' : name.charAt(0)).toUpperCase();
                  const isOwn = currentUser?.email && item.created_by?.email === currentUser.email;
                  return (
                    <div
                      key={item._id || `${item.created_at}-${idx}`}
                      className={`order-edit-chat-bubble-wrap ${isOwn ? 'order-edit-chat-bubble-own' : ''}`}
                    >
                      {!isOwn && (
                        <div className={`order-edit-chat-avatar ${isSystem ? 'order-edit-chat-avatar-kosha' : ''}`}>
                          {isSystem ? (
                            <img src={`${process.env.PUBLIC_URL || ''}/KOSHA-logo.png`} alt="Kosha" />
                          ) : (
                            initial
                          )}
                        </div>
                      )}
                      <div className="order-edit-chat-bubble">
                        <div className="order-edit-chat-bubble-header">
                          <span className="order-edit-chat-name">{isOwn ? 'You' : name}</span>
                          <span className="order-edit-chat-time">
                            {dayjs(item.created_at).isSame(dayjs(), 'day')
                              ? `Today ${dayjs(item.created_at).format('h:mm A')}`
                              : dayjs(item.created_at).format('MMM D, YYYY h:mm A')}
                          </span>
                        </div>
                        <div className="order-edit-chat-bubble-text" style={{ whiteSpace: 'pre-line' }}>{linkifyText(item.text)}</div>
                        {item.attachments && item.attachments.length > 0 && (
                          <CommentAttachments attachments={item.attachments} className="order-edit-chat-bubble-attachments" />
                        )}
                      </div>
                      {isOwn && <div className="order-edit-chat-avatar order-edit-chat-avatar-own">{initial}</div>}
                    </div>
                  );
                })
              ) : (
                <Empty description="No comments yet" image={Empty.PRESENTED_IMAGE_SIMPLE} className="order-edit-chat-empty" />
              )}
            </div>
            <div className="order-edit-comments-add">
              <Input.TextArea
                rows={2}
                placeholder="Type a message..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
                className="order-edit-comment-input"
                autoSize={{ minRows: 2, maxRows: 4 }}
                maxLength={500}
              />
              <div className="order-edit-comment-footer">
                <CommentAttachmentInput
                  value={commentAttachments}
                  onChange={setCommentAttachments}
                  disabled={commentLoading}
                />
                <span className="order-edit-comment-count">{commentText.length}/500 characters</span>
                <Button
                  type="text"
                  shape="circle"
                  icon={<SendOutlined className="order-edit-comment-icon" />}
                  onClick={handleAddComment}
                  loading={commentLoading}
                  disabled={!commentText?.trim()}
                  className="order-edit-comment-add-btn"
                  aria-label="Add comment"
                />
              </div>
            </div>
          </div>
        </Card>

        <Modal
          title="Select items for PDF"
          open={pdfModalOpen}
          onCancel={() => setPdfModalOpen(false)}
          footer={[
            <Button key="cancel" onClick={() => setPdfModalOpen(false)}>Cancel</Button>,
            <Button key="pdf" type="primary" icon={<FilePdfOutlined />} onClick={generatePdf} className="app-btn-primary">
              Generate PDF
            </Button>,
          ]}
          width={400}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space>
              <Button size="small" onClick={selectAllPdfItems}>Select all</Button>
              <Button size="small" onClick={clearAllPdfItems}>Clear all</Button>
            </Space>
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              {orderList.map((item, idx) => (
                <div key={item._id || idx} style={{ marginBottom: 4 }}>
                  <Checkbox
                    checked={selectedPdfIndices.includes(idx)}
                    onChange={() => togglePdfItem(idx)}
                  >
                    Item {idx + 1}{item.itemName ? ` — ${item.itemName}` : ''}
                  </Checkbox>
                </div>
              ))}
            </div>
          </Space>
        </Modal>
      </Space>
    </div>
  );
}
